import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ChartLineUp, PaperPlaneRight, Envelope, Users, Phone, TrendUp, Fire, CurrencyInr } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api, { apiErrMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const todayIST = () => {
  const now = new Date();
  const ist = new Date(now.getTime() + (5.5 * 60 - now.getTimezoneOffset()) * 60000);
  return ist.toISOString().slice(0, 10);
};

export default function DSRReport() {
  const [date, setDate] = useState(todayIST());
  const [dsr, setDsr] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([
        api.get("/reports/dsr", { params: { date } }),
        api.get("/reports/dsr/prefs"),
      ]);
      setDsr(d.data); setRecipients(p.data.recipients || []);
    } catch (e) { toast.error(apiErrMsg(e, "Load failed")); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const saveRecipients = async () => {
    try {
      await api.put("/reports/dsr/prefs", { recipients });
      toast.success("Recipients saved · DSR goes out daily 7 PM IST");
    } catch (e) { toast.error(apiErrMsg(e, "Save failed")); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports/dsr/send-now", recipients);
      if (data.ok) toast.success(`DSR sent · admin + each sales person`);
      else toast.error(`Send failed: ${data.reason || "unknown"} — check Gmail connection`);
    } catch (e) { toast.error(apiErrMsg(e, "Send failed")); }
    finally { setSending(false); }
  };

  const t = dsr?.total || {};
  const perUser = dsr?.per_user || {};
  const users = Object.keys(perUser);

  return (
    <Layout
      title="Daily Sales Report"
      subtitle="User-wise pipeline activity · auto-emailed every day 7:00 PM IST"
      actions={
        <>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 rounded-none" data-testid="dsr-date"/>
          <button onClick={sendNow} disabled={sending} className="inline-flex items-center gap-2 px-3 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50" data-testid="dsr-send-now">
            <PaperPlaneRight size={14}/> {sending ? "Sending…" : "Send DSR now"}
          </button>
        </>
      }
    >
      {/* Totals band */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 border border-black/10 bg-white mb-6" data-testid="dsr-totals">
        <Kpi l="New leads" v={t.new_leads_today || 0} icon={<Users size={14}/>}/>
        <Kpi l="Calls" v={t.calls_today || 0} icon={<Phone size={14}/>} c="#0D9488"/>
        <Kpi l="Proposals" v={t.proposals_sent_today || 0} icon={<Envelope size={14}/>} c="#F4A261"/>
        <Kpi l="Status Δ" v={t.status_changes_today || 0} icon={<TrendUp size={14}/>}/>
        <Kpi l="Open (cum)" v={t.open_leads || 0}/>
        <Kpi l="Hot (cum)" v={t.hot_leads || 0} c="#D90429" icon={<Fire size={14}/>}/>
        <Kpi l="Value won" v={`₹${(t.won_value_today || 0).toLocaleString("en-IN")}`} c="#0D9488" icon={<CurrencyInr size={14}/>}/>
      </div>

      {/* User-wise table */}
      <div className="border border-black/10 bg-white overflow-x-auto mb-6" data-testid="dsr-table">
        <div className="px-5 py-3 border-b border-black/10 flex items-center gap-2">
          <ChartLineUp size={16} className="text-[#D90429]"/>
          <span className="font-display text-lg font-black tracking-tight">User-wise breakdown · {date}</span>
        </div>
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-[#F5F6F8] text-left">
            <tr>{["Sales person", "New", "Calls", "Proposals", "Status Δ", "Open (cum)", "Hot (cum)", "Value won today"].map(h => (
              <th key={h} className="px-3 py-2 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan="8" className="px-3 py-10 text-center text-[#717680] text-sm">No activity yet. Assign leads to your team and log calls to see this table populate.</td></tr>
            )}
            {users.map((email) => {
              const v = perUser[email];
              return (
                <tr key={email} className="border-t border-black/5" data-testid={`dsr-row-${email}`}>
                  <td className="px-3 py-2.5 font-semibold">{email}</td>
                  <td className="px-3 py-2.5 font-mono-shelv">{v.new_leads_today}</td>
                  <td className="px-3 py-2.5 font-mono-shelv text-[#0D9488]">{v.calls_today}</td>
                  <td className="px-3 py-2.5 font-mono-shelv text-[#F4A261]">{v.proposals_sent_today}</td>
                  <td className="px-3 py-2.5 font-mono-shelv">{v.status_changes_today}</td>
                  <td className="px-3 py-2.5 font-mono-shelv">{v.open_leads}</td>
                  <td className="px-3 py-2.5 font-mono-shelv text-[#D90429] font-bold">{v.hot_leads}</td>
                  <td className="px-3 py-2.5 font-mono-shelv text-[#0D9488]">₹{Number(v.won_value_today || 0).toLocaleString("en-IN")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Recipients + delivery config */}
      <div className="border border-black/10 bg-white p-5" data-testid="dsr-prefs">
        <div className="flex items-center gap-2 mb-3">
          <Envelope size={16} className="text-[#D90429]"/>
          <div className="font-display text-base font-black tracking-tight">Consolidated DSR recipients</div>
        </div>
        <p className="text-[11px] text-[#717680] mb-3 leading-relaxed">
          Each sales person gets their own DSR at 7:00 PM IST daily. The consolidated report below goes to these recipients (typically admins).
        </p>
        <Label className="text-xs">Recipients (comma separated)</Label>
        <Input value={recipients.join(", ")} onChange={(e) => setRecipients(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="info@shelvestech.com" className="rounded-none" data-testid="dsr-recipients"/>
        <button onClick={saveRecipients} className="mt-2 px-3 py-1.5 border border-black/20 text-xs font-semibold hover:bg-black hover:text-white" data-testid="dsr-save-prefs">Save recipients</button>
      </div>
    </Layout>
  );
}

function Kpi({ l, v, c = "#0A0A0A", icon }) {
  return (
    <div className="p-4 border-r border-black/5 last:border-r-0">
      <div className="flex items-center gap-1.5 font-mono-shelv text-[9px] tracking-widest uppercase text-[#717680]">{icon}{l}</div>
      <div className="font-display text-2xl font-black tracking-tighter mt-1" style={{ color: c }}>{typeof v === "number" ? v.toLocaleString("en-IN") : v}</div>
    </div>
  );
}
